import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Avatar,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Person,
  Lock,
  Visibility,
  VisibilityOff,
  Agriculture,
} from '@mui/icons-material';
import SSOService from '../services/ssoService';

interface LoginPageProps {
  onLogin: (userId: string, password: string, rememberMe: boolean) => Promise<void>;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoLoginMessage, setAutoLoginMessage] = useState('');

  // 쿠키 기반 자동 로그인 체크 (빠른 로드를 위해 비활성화)
  useEffect(() => {
    // 자동 로그인 체크를 비활성화하여 로그인 페이지가 빠르게 로드되도록 함
    console.log('📝 Login page loaded - auto-login check disabled for fast loading');
    setLoading(false);
    setAutoLoginMessage('');

    // 선택적 자동 로그인 체크 (오래 걸리는 경우 비활성화)
    /*
    const checkServerAuth = async () => {
      try {
        console.log('🔍 Checking server authentication status...');
        setAutoLoginMessage('인증 상태 확인 중...');
        setLoading(true);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3초 타임아웃

        const refreshResponse = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (refreshResponse.ok) {
          const tokenData = await refreshResponse.json();
          const userResponse = await fetch('/api/v1/auth/me', {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokenData.access_token}`,
            },
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            console.log('✅ Auto-login successful');
            window.location.href = '/dashboard';
            return;
          }
        }

        console.log('ℹ️ No valid session found');
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('❌ Auth check failed:', error);
        }
      } finally {
        setLoading(false);
        setAutoLoginMessage('');
      }
    };

    // 자동 로그인 체크를 선택적으로 실행
    // checkServerAuth();
    */
  }, []);

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError('사용자명과 비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onLogin(username, password, rememberMe);
    } catch (error: any) {
      setError(error.response?.data?.detail || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FF6B9D 0%, #E91E63 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 6 }}>
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  boxShadow: '0 8px 20px rgba(255, 107, 157, 0.3)',
                }}
              >
                <Agriculture sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  mb: 1,
                  fontFamily: '"Noto Sans KR", "Roboto", sans-serif',
                }}
              >
                SMART FARM
              </Typography>
              <Typography variant="body1" color="text.secondary">
                스마트팜 통합 관리 시스템
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Auto Login Message */}
            {autoLoginMessage && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                {autoLoginMessage}
              </Alert>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="사용자명"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
                placeholder="사용자명을 입력하세요"
              />

              <TextField
                fullWidth
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 4,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
                placeholder="비밀번호를 입력하세요"
              />
            </Box>

            {/* Remember Me Checkbox */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    자동로그인 (로그인 상태 유지)
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Box>

            {/* Login Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={loading}
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #FF6B9D 0%, #E91E63 100%)',
                boxShadow: '0 4px 15px rgba(255, 107, 157, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #E91E63 0%, #C2185B 100%)',
                  boxShadow: '0 6px 20px rgba(255, 107, 157, 0.6)',
                },
                '&:disabled': {
                  background: 'rgba(0,0,0,0.12)',
                  boxShadow: 'none',
                },
                mb: 3,
              }}
            >
              {loading ? '로그인 중...' : '로그인'}
            </Button>

            {/* Demo Credentials */}
            <Box
              sx={{
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                데모 계정 (개발용)
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                사용자명: admin, user123, manager01
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                비밀번호: 임의 입력 (백엔드 연동 전)
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 'bold',
              mb: 1,
              fontFamily: '"Noto Sans KR", "Roboto", sans-serif'
            }}
          >
            포닉스텍 (https://ponicstec.com)
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              mb: 1
            }}
          >
            농업법인회사 착한농부들 주식회사
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.7)'
            }}
          >
            2023 Copyright PONICSTEC All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;